import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input } from '@artosera/ui';
import { ApiError } from '../../../../lib/api.js';
import {
  apercuCorrespondance,
  choisirCorrespondance,
  creerProfil,
  erreursDeRegles,
  lireCibles,
  lireProfil,
  lireSurcharges,
  NATURES_PIVOT,
  publierVersionProfil,
  SURCHARGES_VIDES,
  type BlocNature,
  type ChampRegle,
  type CiblesPivot,
  type ErreurRegle,
  type NaturePivot,
  type ProfilComplet,
  type ProfilSources,
  type Regles,
  type Surcharges,
} from '../../../../lib/correspondance.js';
import {
  blocVide,
  cheminChamp,
  champsSansFormatDate,
  colonneAmbigueDuChamp,
  colonneProfilee,
  copier,
  erreursSous,
  obligatoiresManquantes,
  reglesAEnvoyer,
  reglesVides,
  surchargesAEnvoyer,
  surchargesVides,
  tableDuRole,
  type ContexteSources,
} from '../../../../lib/reglesEdition.js';
import type { LotReprise } from '../../../../lib/reprise.js';
import formStyles from '../../../../styles/form.module.css';
import { messageReprise } from '../messages.js';
import reprise from '../Reprise.module.css';
import { ApercuVue } from './ApercuVue.js';
import { ChampEditeur } from './ChampEditeur.js';
import { ChampsCibles } from './ChampsCibles.js';
import styles from './Correspondance.module.css';
import { ReglesGenerales, type AideSources } from './ReglesGenerales.js';
import { SurchargesEditeur } from './SurchargesEditeur.js';

/** L'aperçu attend la fin de la frappe : un calcul par pause, pas par lettre. */
const ATTENTE_APERCU_MS = 400;

function useDiffere<T>(valeur: T, attente: number): T {
  const [differe, setDiffere] = useState(valeur);
  useEffect(() => {
    const minuterie = window.setTimeout(() => setDiffere(valeur), attente);
    return () => window.clearTimeout(minuterie);
  }, [valeur, attente]);
  return differe;
}

export const cleProfils = ['console', 'profils'] as const;

/** Les en-têtes que les règles lisent dans la source principale : la signature d'un nouveau profil. */
function signatureDesRegles(regles: Regles, contexte: ContexteSources): string[] {
  const principale = tableDuRole(contexte, null)?.entetes ?? [];
  const lus = new Set<string>();
  for (const bloc of Object.values(regles.natures))
    for (const champ of Object.values(bloc.colonnes))
      for (const reference of champ.sources)
        if (reference.role === null && !reference.parent && principale.includes(reference.champ))
          lus.add(reference.champ);
  return lus.size > 0 ? [...lus] : principale.filter((entete) => entete.trim() !== '');
}

/** Charge ce que l'éditeur suppose : champs du pivot, version du profil citée, surcharges. */
export function EditeurChargement({
  idGalerie,
  lot,
  profilSources,
  peutAgir,
  peutProfils,
  onLot,
}: {
  idGalerie: string;
  lot: LotReprise;
  profilSources: ProfilSources;
  peutAgir: boolean;
  peutProfils: boolean;
  onLot: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const cibles = useQuery({ queryKey: [...cleProfils, 'cibles'], queryFn: lireCibles });
  const profil = useQuery({
    queryKey: [...cleProfils, lot.profil?.id ?? '', lot.profil?.version ?? 0],
    queryFn: () => lireProfil(lot.profil!.id, lot.profil!.version),
    enabled: lot.profil != null,
  });
  const surcharges = useQuery({
    queryKey: [
      'console',
      'galerie',
      idGalerie,
      'reprise',
      'lot',
      lot.id,
      'surcharges',
      lot.revision,
    ],
    queryFn: () => lireSurcharges(idGalerie, lot.id),
    enabled: lot.surcharges === true,
  });

  const erreur = cibles.error ?? profil.error ?? surcharges.error;
  if (erreur)
    return (
      <p className={reprise.erreur} role="alert">
        {messageReprise(t, erreur) ?? t('correspondance.editeur.erreurChargement')}
      </p>
    );
  if (
    !cibles.data ||
    (lot.profil != null && !profil.data) ||
    (lot.surcharges === true && surcharges.isLoading)
  )
    return <p className={reprise.discret}>{t('reprise.chargement')}</p>;

  return (
    <EditeurCorrespondance
      // Un autre profil, ou une autre version : l'éditeur repart de ses règles.
      key={lot.profil ? `${lot.profil.id}@${String(lot.profil.version)}` : 'aucun'}
      idGalerie={idGalerie}
      lot={lot}
      profilSources={profilSources}
      cibles={cibles.data}
      profil={profil.data ?? null}
      surchargesBase={surcharges.data ?? SURCHARGES_VIDES}
      peutAgir={peutAgir}
      peutProfils={peutProfils}
      onLot={onLot}
    />
  );
}

/**
 * L'éditeur de correspondance : à gauche les champs cibles de la nature, à
 * droite la source et les transformations du champ choisi, puis l'aperçu en
 * direct et les choix propres à la galerie. Sous 768 px les deux volets se
 * suivent : la liste d'abord, le détail d'un champ ensuite.
 *
 * Enregistrer ne change jamais le lot d'office : une nouvelle version d'un
 * profil naît à côté de l'ancienne, et le lot garde celle qu'il cite jusqu'à
 * ce qu'on lui demande de passer à la nouvelle.
 */
export function EditeurCorrespondance({
  idGalerie,
  lot,
  profilSources,
  cibles,
  profil,
  surchargesBase,
  peutAgir,
  peutProfils,
  onLot,
}: {
  idGalerie: string;
  lot: LotReprise;
  profilSources: ProfilSources;
  cibles: CiblesPivot;
  profil: ProfilComplet | null;
  surchargesBase: Surcharges;
  peutAgir: boolean;
  /** `console:manage-import-profiles` : enregistrer un profil ou une version. */
  peutProfils: boolean;
  onLot: (lot: LotReprise) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const client = useQueryClient();

  const reglesDeBase = useMemo(
    () => profil?.regles ?? reglesVides(lot.nature),
    [profil, lot.nature],
  );
  const [regles, setRegles] = useState<Regles>(() => copier(reglesDeBase));
  const [surcharges, setSurcharges] = useState<Surcharges>(() => copier(surchargesBase));
  const [nature, setNature] = useState<string>(lot.nature);
  const [nom, setNom] = useState(profil?.nom ?? '');
  const [application, setApplication] = useState(profil?.application ?? '');
  const [message, setMessage] = useState<string | null>(null);
  const [erreursSauvegarde, setErreursSauvegarde] = useState<ErreurRegle[]>([]);
  const [erreursSurcharges, setErreursSurcharges] = useState<ErreurRegle[]>([]);
  const [publie, setPublie] = useState<ProfilComplet | null>(null);

  const natures = Object.keys(regles.natures);
  const natureActive = natures.includes(nature) ? nature : (natures[0] ?? lot.nature);
  const bloc = regles.natures[natureActive] ?? blocVide();
  const cible = cibles.natures.find((candidate) => candidate.nature === natureActive);
  const [selection, setSelection] = useState<string | null>(() => {
    const depart = cibles.natures.find((candidate) => candidate.nature === lot.nature);
    const premierManque = depart
      ? obligatoiresManquantes(regles.natures[lot.nature] ?? blocVide(), depart)[0]
      : undefined;
    return premierManque ?? depart?.colonnes[0]?.cle ?? null;
  });
  const [detail, setDetail] = useState(false);

  const contexte: ContexteSources = useMemo(
    () => ({ regles, profil: profilSources, sources: lot.sources, surcharges }),
    [regles, profilSources, lot.sources, surcharges],
  );

  // ── Aide à la saisie, tirée du profil des sources ──────────────────────
  const aide: AideSources = useMemo(() => {
    const principal = profilSources.fichiers.find(
      (fichier) =>
        fichier.fichierId ===
        (surcharges.fichiers[regles.sources[0]?.role ?? ''] ?? lot.sources[0]?.fichierId ?? null),
    );
    return {
      colonnes: (role) => tableDuRole(contexte, role)?.entetes ?? [],
      tables: (role) => {
        const rang = regles.sources.findIndex((source) => source.role === role);
        const fichierId = surcharges.fichiers[role] ?? lot.sources[rang]?.fichierId ?? null;
        const fichier = profilSources.fichiers.find((candidat) => candidat.fichierId === fichierId);
        return (fichier?.tables ?? []).flatMap((table) => (table.nom ? [table.nom] : []));
      },
      tableaux: (principal?.chemins ?? []).filter((chemin) => chemin.tableau).map((c) => c.chemin),
    };
  }, [contexte, lot.sources, profilSources.fichiers, regles.sources, surcharges.fichiers]);
  const roles = regles.sources.map((source) => source.role);
  const toutesLesColonnes = useMemo(
    () => [...new Set(roles.flatMap((role, rang) => aide.colonnes(rang === 0 ? null : role)))],
    [aide, roles],
  );
  const chemins = useMemo(
    () => [
      ...profilSources.fichiers.flatMap((fichier) => fichier.chemins ?? []).map((c) => c.chemin),
      ...toutesLesColonnes,
    ],
    [profilSources.fichiers, toutesLesColonnes],
  );

  // ── Aperçu en direct ───────────────────────────────────────────────────
  const envoi = useMemo(
    () => ({ regles: reglesAEnvoyer(regles), surcharges: surchargesAEnvoyer(surcharges) }),
    [regles, surcharges],
  );
  const envoiDiffere = useDiffere(envoi, ATTENTE_APERCU_MS);
  const apercu = useQuery({
    queryKey: [
      'console',
      'galerie',
      idGalerie,
      'reprise',
      'lot',
      lot.id,
      'apercu',
      JSON.stringify(envoiDiffere),
    ],
    queryFn: () => apercuCorrespondance(idGalerie, lot.id, envoiDiffere),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: Infinity,
    gcTime: 60_000,
  });
  const refusApercu = erreursDeRegles(apercu.error);
  const erreurApercu =
    apercu.isError && refusApercu.length === 0 && apercu.error instanceof ApiError
      ? messageReprise(t, apercu.error)
      : null;

  // ── Erreurs, par champ ─────────────────────────────────────────────────
  const dateAmbigue = champsSansFormatDate(contexte).map((manque) => ({
    chemin: manque.chemin,
    message: t('correspondance.erreurs.dateAmbigue', { colonne: manque.colonne }),
  }));
  const erreurs: ErreurRegle[] = [...erreursSauvegarde, ...refusApercu, ...dateAmbigue];
  const erreursParChamp = new Map<string, number>();
  for (const erreur of erreurs) {
    const trouve = new RegExp(`^natures\\.${natureActive}\\.colonnes\\.([^.]+)`).exec(
      erreur.chemin,
    );
    if (trouve?.[1]) erreursParChamp.set(trouve[1], (erreursParChamp.get(trouve[1]) ?? 0) + 1);
  }
  const refusParColonne = new Map<string, number>();
  for (const erreurLigne of apercu.data?.erreurs ?? [])
    if (erreurLigne.nature === natureActive && erreurLigne.colonne)
      refusParColonne.set(erreurLigne.colonne, (refusParColonne.get(erreurLigne.colonne) ?? 0) + 1);
  const erreursDeLaNature = erreurs.filter(
    (erreur) => erreur.chemin === `natures.${natureActive}.colonnes`,
  );

  // ── Modifications ──────────────────────────────────────────────────────
  const majBloc = (suite: BlocNature): void =>
    setRegles((courantes) => ({
      ...courantes,
      natures: { ...courantes.natures, [natureActive]: suite },
    }));
  const majChamp = (cle: string, champ: ChampRegle | null): void => {
    const colonnes = { ...bloc.colonnes };
    if (champ === null) delete colonnes[cle];
    else colonnes[cle] = champ;
    majBloc({ ...bloc, colonnes });
    setErreursSauvegarde((courantes) =>
      courantes.filter((e) => e.chemin !== cheminChamp(natureActive, cle)),
    );
  };
  const ajouterNature = (valeur: NaturePivot): void => {
    setRegles((courantes) => ({
      ...courantes,
      natures: { ...courantes.natures, [valeur]: blocVide() },
    }));
    setNature(valeur);
    setSelection(null);
    setDetail(false);
  };
  const retirerNature = (valeur: string): void => {
    setRegles((courantes) => {
      const suite = { ...courantes.natures };
      delete suite[valeur];
      return { ...courantes, natures: suite };
    });
    setNature(lot.nature);
    setSelection(null);
  };

  const modifie =
    JSON.stringify(reglesAEnvoyer(regles)) !== JSON.stringify(reglesAEnvoyer(reglesDeBase));
  const surchargesModifiees =
    JSON.stringify(surchargesAEnvoyer(surcharges)) !==
    JSON.stringify(surchargesAEnvoyer(surchargesBase));
  const manqueFormatDate = dateAmbigue.length > 0;

  // ── Enregistrements ────────────────────────────────────────────────────
  const echec = (cause: unknown, surSurcharges = false): void => {
    const champs = erreursDeRegles(cause);
    if (surSurcharges) setErreursSurcharges(champs);
    else setErreursSauvegarde(champs);
    setMessage(messageReprise(t, cause));
  };
  const reussi = (enregistre: ProfilComplet): void => {
    setMessage(null);
    setErreursSauvegarde([]);
    setPublie(enregistre);
    void client.invalidateQueries({ queryKey: cleProfils });
  };
  const bloquer = (): boolean => {
    if (manqueFormatDate) {
      setMessage(t('correspondance.erreurs.formatDateManquant'));
      return true;
    }
    return false;
  };

  const nouveauProfil = useMutation({
    mutationFn: () =>
      creerProfil({
        nom: nom.trim(),
        application: application.trim(),
        nature: lot.nature as NaturePivot,
        signature: signatureDesRegles(regles, contexte),
        regles: reglesAEnvoyer(regles),
      }),
    onSuccess: reussi,
    onError: (cause) => echec(cause),
  });
  const nouvelleVersion = useMutation({
    mutationFn: () => {
      if (!profil) throw new Error('Pas de profil à versionner.');
      return publierVersionProfil(profil.id, {
        nom: nom.trim(),
        application: application.trim(),
        signature: signatureDesRegles(regles, contexte),
        regles: reglesAEnvoyer(regles),
        version: profil.version,
      });
    },
    onSuccess: reussi,
    onError: (cause) => echec(cause),
  });
  const utiliser = useMutation({
    mutationFn: (enregistre: ProfilComplet) =>
      choisirCorrespondance(idGalerie, lot.id, {
        revision: lot.revision,
        profil: { id: enregistre.id, version: enregistre.version },
        // Un nouveau profil retire les surcharges, sauf si on les renvoie.
        ...(surchargesVides(surcharges) ? {} : { surcharges: surchargesAEnvoyer(surcharges) }),
      }),
    onSuccess: (suite) => {
      setPublie(null);
      setMessage(null);
      onLot(suite);
    },
    onError: (cause) => echec(cause),
  });
  const enregistrerSurcharges = useMutation({
    mutationFn: () =>
      choisirCorrespondance(idGalerie, lot.id, {
        revision: lot.revision,
        surcharges: surchargesVides(surcharges) ? null : surchargesAEnvoyer(surcharges),
      }),
    onSuccess: (suite) => {
      setErreursSurcharges([]);
      setMessage(null);
      onLot(suite);
    },
    onError: (cause) => echec(cause, true),
  });

  const occupe =
    nouveauProfil.isPending ||
    nouvelleVersion.isPending ||
    utiliser.isPending ||
    enregistrerSurcharges.isPending;
  const nomValide = nom.trim() !== '';
  const naturesAjoutables = NATURES_PIVOT.filter((valeur) => !natures.includes(valeur));
  const colonneChoisie = cible?.colonnes.find((colonne) => colonne.cle === selection) ?? null;
  const champChoisi = colonneChoisie ? bloc.colonnes[colonneChoisie.cle] : undefined;
  const premiereSource = champChoisi?.sources[0];

  return (
    <div className={reprise.pile}>
      <div className={reprise.rangee}>
        <h3 className={reprise.sousTitre}>
          {profil
            ? t('correspondance.editeur.partDe', { nom: profil.nom, version: profil.version })
            : t('correspondance.editeur.partDeRien')}
        </h3>
        {modifie ? <Badge tone="reserved">{t('correspondance.editeur.modifie')}</Badge> : null}
      </div>

      <div className={styles.natures} role="group" aria-label={t('correspondance.editeur.natures')}>
        {natures.map((valeur) => (
          <span key={valeur} className={styles.nature}>
            <button
              type="button"
              className={styles.natureBouton}
              aria-pressed={valeur === natureActive}
              onClick={() => {
                setNature(valeur);
                setSelection(null);
                setDetail(false);
              }}
            >
              {t(`reprise.natures.${valeur}`)}
            </button>
            {valeur !== lot.nature ? (
              <button
                type="button"
                className={styles.natureRetirer}
                aria-label={t('correspondance.editeur.retirerNature', {
                  nature: t(`reprise.natures.${valeur}`),
                })}
                onClick={() => retirerNature(valeur)}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        {naturesAjoutables.length > 0 ? (
          <span className={styles.nature}>
            <label htmlFor="corr-ajout-nature" className={styles.sansTexte}>
              {t('correspondance.editeur.ajouterNature')}
            </label>
            <select
              id="corr-ajout-nature"
              className={styles.selection}
              value=""
              onChange={(e) => e.target.value && ajouterNature(e.target.value as NaturePivot)}
            >
              <option value="">{t('correspondance.editeur.ajouterNature')}</option>
              {naturesAjoutables.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {t(`reprise.natures.${valeur}`)}
                </option>
              ))}
            </select>
          </span>
        ) : null}
      </div>

      <ReglesGenerales
        regles={regles}
        onRegles={setRegles}
        nature={natureActive}
        bloc={bloc}
        onBloc={majBloc}
        cible={cible}
        aide={aide}
        erreurs={erreurs}
      />

      {erreursDeLaNature.length > 0 ? (
        <ul className={styles.erreursChamp} role="alert">
          {erreursDeLaNature.map((erreur) => (
            <li key={erreur.message}>{erreur.message}</li>
          ))}
        </ul>
      ) : null}

      {cible ? (
        <div className={styles.deuxVolets} data-vue={detail ? 'detail' : 'liste'}>
          <ChampsCibles
            cible={cible}
            bloc={bloc}
            selection={selection}
            onSelectionner={(cle) => {
              setSelection(cle);
              setDetail(true);
            }}
            erreursParChamp={erreursParChamp}
            refusParColonne={refusParColonne}
          />
          <div className={styles.volet}>
            {colonneChoisie ? (
              <ChampEditeur
                key={`${natureActive}.${colonneChoisie.cle}`}
                colonne={colonneChoisie}
                champ={champChoisi}
                onChange={(suite) => majChamp(colonneChoisie.cle, suite)}
                // La date ambiguë se dit dans la note du champ, avec son action : la redire ici la doublerait.
                erreurs={erreursSous(erreurs, cheminChamp(natureActive, colonneChoisie.cle))
                  .filter((erreur) => !dateAmbigue.includes(erreur))
                  .map((erreur) => erreur.message)}
                suggestions={toutesLesColonnes}
                chemins={chemins}
                roles={roles}
                avecParent={regles.deplier !== null}
                colonneAmbigue={champChoisi ? colonneAmbigueDuChamp(champChoisi, contexte) : null}
                echantillon={premiereSource ? colonneProfilee(contexte, premiereSource) : null}
                onRetour={() => setDetail(false)}
              />
            ) : (
              <p className={reprise.discret}>{t('correspondance.editeur.choisirChamp')}</p>
            )}
          </div>
        </div>
      ) : null}

      <ApercuVue
        apercu={apercu.data}
        chargement={apercu.isFetching || envoi !== envoiDiffere}
        refusees={refusApercu}
        erreur={erreurApercu}
      />

      <SurchargesEditeur
        regles={regles}
        cibles={cibles}
        surcharges={surcharges}
        onChange={setSurcharges}
        sources={lot.sources}
        erreurs={erreursSurcharges}
        peutEnregistrer={peutAgir}
        raisonBloquee={lot.profil == null ? t('correspondance.surcharges.profilRequis') : null}
        enregistrement={enregistrerSurcharges.isPending}
        onEnregistrer={() => enregistrerSurcharges.mutate()}
        modifie={surchargesModifiees}
      />

      {peutProfils ? (
        <section className={styles.enregistrer} aria-labelledby="corr-enregistrer">
          <h3 id="corr-enregistrer" className={reprise.sousTitre}>
            {t('correspondance.enregistrer.titre')}
          </h3>
          <p className={reprise.texte}>{t('correspondance.enregistrer.texte')}</p>
          <div className={styles.grilleEnregistrer}>
            <div className={formStyles.champ}>
              <label htmlFor="corr-nom">{t('correspondance.enregistrer.nom')}</label>
              <Input
                id="corr-nom"
                value={nom}
                maxLength={120}
                onChange={(e) => setNom(e.target.value)}
              />
            </div>
            <div className={formStyles.champ}>
              <label htmlFor="corr-application">
                {t('correspondance.enregistrer.application')}
              </label>
              <Input
                id="corr-application"
                value={application}
                maxLength={200}
                onChange={(e) => setApplication(e.target.value)}
              />
              <span className={formStyles.aide}>
                {t('correspondance.enregistrer.applicationAide')}
              </span>
            </div>
          </div>
          {message ? (
            <p className={reprise.erreur} role="alert">
              {message}
            </p>
          ) : null}
          <div className={reprise.actions}>
            <Button
              variant="secondary"
              loading={nouveauProfil.isPending}
              disabled={occupe || !nomValide}
              onClick={() => {
                if (!bloquer()) nouveauProfil.mutate();
              }}
            >
              {t('correspondance.enregistrer.nouveau')}
            </Button>
            {profil ? (
              <Button
                loading={nouvelleVersion.isPending}
                disabled={occupe || !nomValide || !modifie}
                onClick={() => {
                  if (!bloquer()) nouvelleVersion.mutate();
                }}
              >
                {t('correspondance.enregistrer.version')}
              </Button>
            ) : null}
          </div>
          {!nomValide ? (
            <p className={reprise.discret}>{t('correspondance.enregistrer.nomRequis')}</p>
          ) : null}
          {profil && !modifie ? (
            <p className={reprise.discret}>{t('correspondance.enregistrer.aucuneModification')}</p>
          ) : null}
          {publie ? (
            <div className={styles.publie} role="status">
              <p>
                {t('correspondance.enregistrer.publie', {
                  nom: publie.nom,
                  version: publie.version,
                })}{' '}
                {lot.profil
                  ? t('correspondance.enregistrer.lotGarde', { version: lot.profil.version })
                  : t('correspondance.enregistrer.lotSansProfil')}
              </p>
              {lot.profil?.id !== publie.id || lot.profil.version !== publie.version ? (
                <div className={reprise.actions}>
                  <Button loading={utiliser.isPending} onClick={() => utiliser.mutate(publie)}>
                    {t('correspondance.enregistrer.utiliser', { version: publie.version })}
                  </Button>
                </div>
              ) : null}
              {lot.transformation?.fait ? (
                <p className={reprise.discret}>{t('correspondance.enregistrer.defait')}</p>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
